/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
  Share,
  Dimensions,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

const { width } = Dimensions.get('window');

const LearningCertificateScreen = () => {
  const navigation = useNavigation();
  const [userData, setUserData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Mixed color scheme for different certificate types
  const certificateColors = {
    'Civil Engineering': '#f59e0b', // Orange
    'Mechanical Engineering': '#10b981', // Green
    'Electrical Engineering': '#3b82f6', // Blue
    'Software Engineering': '#8b5cf6', // Purple
    'Chemical Engineering': '#ef4444', // Red
    'Environmental Engineering': '#06b6d4', // Cyan
    'Project Management': '#f97316', // Orange variant
    'Health & Safety': '#14b8a6', // Teal
    'Materials Science': '#84cc16', // Lime
    'Data Science': '#6366f1', // Indigo
  };

  const getColorForCertificate = (category) => {
    return certificateColors[category] || '#f59e0b';
  };

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const storedUser = await AsyncStorage.getItem('user');
      const storedUserData = await AsyncStorage.getItem('userData');
      
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserData(parsedUser);
      } else if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const getUserName = () => {
    if (!userData) return 'Learning Student';
    return userData.full_name || userData.fullName || userData.name || 
           `${userData.first_name || userData.firstName || ''} ${userData.last_name || userData.lastName || ''}`.trim() || 
           'Learning Student';
  };

  // Sample certificates data
  const certificates = [
    {
      id: '1',
      title: 'Civil Engineering Fundamentals',
      subtitle: 'Structural Analysis & Design',
      category: 'Civil Engineering',
      instructor: 'Dr. Sarah Johnson',
      institution: 'JOBENG Learning Platform',
      completedDate: '2024-12-15',
      grade: 'A',
      credentialId: 'CE-2024-001',
      verificationUrl: 'https://jobeng.com/verify/CE-2024-001',
      skills: ['Structural Analysis', 'Load Calculations', 'Design Principles'],
      duration: '40 hours',
      certificateType: 'Course Completion',
      isVerified: true,
    },
    {
      id: '2',
      title: 'Project Management Essentials',
      subtitle: 'Planning & Execution Strategies',
      category: 'Project Management',
      instructor: 'Dr. Lisa Anderson',
      institution: 'JOBENG Learning Platform',
      completedDate: '2024-12-10',
      grade: 'A+',
      credentialId: 'PM-2024-002',
      verificationUrl: 'https://jobeng.com/verify/PM-2024-002',
      skills: ['Project Planning', 'Risk Management', 'Team Leadership'],
      duration: '32 hours',
      certificateType: 'Professional Certificate',
      isVerified: true,
    },
    {
      id: '3',
      title: 'Health & Safety Fundamentals',
      subtitle: 'Workplace Safety Standards',
      category: 'Health & Safety',
      instructor: 'Prof. Robert Taylor',
      institution: 'JOBENG Learning Platform',
      completedDate: '2024-11-28',
      grade: 'A',
      credentialId: 'HS-2024-003',
      verificationUrl: 'https://jobeng.com/verify/HS-2024-003',
      skills: ['Safety Protocols', 'Risk Assessment', 'Compliance'],
      duration: '24 hours',
      certificateType: 'Safety Certification',
      isVerified: true,
    },
    {
      id: '4',
      title: 'AutoCAD for Engineers',
      subtitle: '2D & 3D Technical Drawing',
      category: 'Software Engineering',
      instructor: 'Prof. Michael Chen',
      institution: 'JOBENG Learning Platform',
      completedDate: '2024-11-20',
      grade: 'B+',
      credentialId: 'SW-2024-004',
      verificationUrl: 'https://jobeng.com/verify/SW-2024-004',
      skills: ['AutoCAD', '2D Drawing', '3D Modeling', 'Technical Documentation'],
      duration: '36 hours',
      certificateType: 'Software Proficiency',
      isVerified: true,
    },
    {
      id: '5',
      title: 'Materials Science Basics',
      subtitle: 'Properties & Applications',
      category: 'Materials Science',
      instructor: 'Dr. Jennifer Lee',
      institution: 'JOBENG Learning Platform',
      completedDate: '2024-11-15',
      grade: 'A-',
      credentialId: 'MS-2024-005',
      verificationUrl: 'https://jobeng.com/verify/MS-2024-005',
      skills: ['Material Properties', 'Testing Methods', 'Selection Criteria'],
      duration: '28 hours',
      certificateType: 'Course Completion',
      isVerified: true,
    },
    {
      id: '6',
      title: 'Python Programming',
      subtitle: 'Data Analysis & Automation',
      category: 'Data Science',
      instructor: 'Prof. Alex Rodriguez',
      institution: 'JOBENG Learning Platform',
      completedDate: '2024-11-10',
      grade: 'A',
      credentialId: 'DS-2024-006',
      verificationUrl: 'https://jobeng.com/verify/DS-2024-006',
      skills: ['Python', 'Data Analysis', 'Automation', 'Pandas', 'NumPy'],
      duration: '45 hours',
      certificateType: 'Programming Certificate',
      isVerified: true,
    },
  ];

  const categories = ['All', ...new Set(certificates.map(cert => cert.category))];

  const filteredCertificates = selectedCategory === 'All' 
    ? certificates 
    : certificates.filter(cert => cert.category === selectedCategory);

  const handleViewCertificate = (certificate) => {
    Alert.alert(
      'View Certificate',
      `View full certificate for "${certificate.title}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'View Certificate',
          onPress: () => {
            // TODO: Navigate to full certificate view or open PDF
            Alert.alert('Certificate Viewer', 'Full certificate viewer will be implemented soon!');
          },
        },
      ]
    );
  };

  const handleDownloadCertificate = (certificate) => {
    Alert.alert(
      'Download Certificate',
      `Download "${certificate.title}" certificate as PDF?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Download',
          onPress: () => {
            // TODO: Implement PDF download
            Alert.alert('Download', 'Certificate download will be implemented soon!');
          },
        },
      ]
    );
  };

  const handleShareCertificate = async (certificate) => {
    try {
      const shareContent = `🎓 I've earned a certificate in ${certificate.title}!\n\n📚 Course: ${certificate.title}\n🏫 Institution: ${certificate.institution}\n👨‍🏫 Instructor: ${certificate.instructor}\n📅 Completed: ${certificate.completedDate}\n🎯 Grade: ${certificate.grade}\n⏱️ Duration: ${certificate.duration}\n\n🔗 Verify: ${certificate.verificationUrl}\n\n#Engineering #Learning #Certificate #JOBENG`;
      
      await Share.share({
        message: shareContent,
        title: `${certificate.title} Certificate`,
      });
    } catch (error) {
      console.error('Error sharing certificate:', error);
    }
  };

  const handleVerifyCertificate = (certificate) => {
    Alert.alert(
      'Verify Certificate',
      `Credential ID: ${certificate.credentialId}\n\nThis will open the verification page in your browser.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Verify Online',
          onPress: () => {
            // TODO: Open verification URL in browser
            Alert.alert('Verification', 'Certificate verification will open in browser soon!');
          },
        },
      ]
    );
  };

  const renderCategoryTabs = () => (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false}
      style={styles.categoryTabs}
      contentContainerStyle={styles.categoryTabsContent}
    >
      {categories.map((category) => (
        <TouchableOpacity
          key={category}
          style={[
            styles.categoryTab,
            selectedCategory === category && [
              styles.activeCategoryTab,
              { backgroundColor: '#f59e0b' }
            ],
          ]}
          onPress={() => setSelectedCategory(category)}
        >
          <Text
            style={[
              styles.categoryTabText,
              selectedCategory === category && styles.activeCategoryTabText,
            ]}
          >
            {category}
          </Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const renderCertificateCard = (certificate) => {
    const certificateColor = getColorForCertificate(certificate.category);
    
    return (
      <View key={certificate.id} style={styles.certificateCard}>
        {/* Certificate Header */}
        <View style={styles.certificateHeader}>
          <View style={[styles.certificateIcon, { backgroundColor: certificateColor }]}>
            <MaterialCommunityIcons name="certificate" size={28} color="#ffffff" />
          </View>
          <View style={styles.certificateInfo}>
            <Text style={styles.certificateTitle}>{certificate.title}</Text>
            <Text style={styles.certificateSubtitle}>{certificate.subtitle}</Text>
            <Text style={styles.certificateInstructor}>by {certificate.instructor}</Text>
          </View>
          {certificate.isVerified && (
            <View style={[styles.verifiedBadge, { backgroundColor: certificateColor }]}>
              <MaterialCommunityIcons name="check-decagram" size={20} color="#ffffff" />
            </View>
          )}
        </View>

        {/* Certificate Details */}
        <View style={styles.certificateDetails}>
          <View style={styles.detailRow}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="calendar-check" size={16} color="#6b7280" />
              <Text style={styles.detailText}>Completed: {certificate.completedDate}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="trophy" size={16} color={certificateColor} />
              <Text style={[styles.detailText, { color: certificateColor, fontWeight: '600' }]}>
                Grade: {certificate.grade}
              </Text>
            </View>
          </View>
          
          <View style={styles.detailRow}>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="clock-outline" size={16} color="#6b7280" />
              <Text style={styles.detailText}>Duration: {certificate.duration}</Text>
            </View>
            <View style={styles.detailItem}>
              <MaterialCommunityIcons name="bookmark" size={16} color="#6b7280" />
              <Text style={styles.detailText}>{certificate.certificateType}</Text>
            </View>
          </View>

          <View style={styles.credentialInfo}>
            <MaterialCommunityIcons name="shield-check" size={16} color={certificateColor} />
            <Text style={styles.credentialText}>ID: {certificate.credentialId}</Text>
          </View>
        </View>

        {/* Skills */}
        <View style={styles.skillsSection}>
          <Text style={styles.skillsTitle}>Skills Demonstrated:</Text>
          <View style={styles.skillsContainer}>
            {certificate.skills.map((skill, index) => (
              <View key={index} style={[styles.skillTag, { borderColor: certificateColor }]}>
                <Text style={[styles.skillText, { color: certificateColor }]}>{skill}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Actions */}
        <View style={styles.certificateActions}>
          <TouchableOpacity
            style={[styles.actionButton, styles.viewButton, { backgroundColor: certificateColor }]}
            onPress={() => handleViewCertificate(certificate)}
          >
            <MaterialCommunityIcons name="eye" size={16} color="#ffffff" />
            <Text style={styles.actionButtonText}>View</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.actionButton, styles.downloadButton, { borderColor: certificateColor }]}
            onPress={() => handleDownloadCertificate(certificate)}
          >
            <MaterialCommunityIcons name="download" size={16} color={certificateColor} />
            <Text style={[styles.actionButtonText, { color: certificateColor }]}>Download</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.actionButton, styles.shareButton]}
            onPress={() => handleShareCertificate(certificate)}
          >
            <MaterialCommunityIcons name="share" size={16} color="#6b7280" />
            <Text style={[styles.actionButtonText, { color: '#6b7280' }]}>Share</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.actionButton, styles.verifyButton]}
            onPress={() => handleVerifyCertificate(certificate)}
          >
            <MaterialCommunityIcons name="shield-check" size={16} color="#10b981" />
            <Text style={[styles.actionButtonText, { color: '#10b981' }]}>Verify</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderSummaryStats = () => (
    <View style={styles.summaryCard}>
      <Text style={styles.summaryTitle}>Certificate Summary</Text>
      <Text style={styles.summarySubtitle}>Hello, {getUserName()}!</Text>
      
      <View style={styles.statsGrid}>
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#f59e0b' }]}>
            <MaterialCommunityIcons name="certificate" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#f59e0b' }]}>{certificates.length}</Text>
          <Text style={styles.statLabel}>Total Certificates</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#10b981' }]}>
            <MaterialCommunityIcons name="trophy" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#10b981' }]}>
            {certificates.filter(cert => cert.grade.includes('A')).length}
          </Text>
          <Text style={styles.statLabel}>A Grades</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#3b82f6' }]}>
            <MaterialCommunityIcons name="clock" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#3b82f6' }]}>
            {certificates.reduce((total, cert) => total + parseInt(cert.duration), 0)}h
          </Text>
          <Text style={styles.statLabel}>Total Hours</Text>
        </View>
        
        <View style={styles.statItem}>
          <View style={[styles.statIcon, { backgroundColor: '#8b5cf6' }]}>
            <MaterialCommunityIcons name="school" size={20} color="#ffffff" />
          </View>
          <Text style={[styles.statNumber, { color: '#8b5cf6' }]}>
            {new Set(certificates.map(cert => cert.category)).size}
          </Text>
          <Text style={styles.statLabel}>Categories</Text>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#f9fafb" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#f59e0b" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>My Certificates</Text>
        <TouchableOpacity style={styles.menuButton}>
          <MaterialCommunityIcons name="dots-vertical" size={24} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {renderSummaryStats()}
        {renderCategoryTabs()}
        
        <View style={styles.certificatesSection}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              {selectedCategory === 'All' ? 'All Certificates' : `${selectedCategory} Certificates`}
            </Text>
            <Text style={styles.sectionCount}>{filteredCertificates.length} certificates</Text>
          </View>
          
          {filteredCertificates.length > 0 ? (
            filteredCertificates.map(renderCertificateCard)
          ) : (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="certificate" size={64} color="#d1d5db" />
              <Text style={styles.emptyStateTitle}>No Certificates Found</Text>
              <Text style={styles.emptyStateText}>
                Complete courses to earn certificates in this category.
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    padding: 5,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#f59e0b',
  },
  menuButton: {
    padding: 5,
  },
  container: {
    flex: 1,
  },
  summaryCard: {
    backgroundColor: '#fff',
    margin: 15,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  summaryTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  summarySubtitle: {
    fontSize: 16,
    color: '#6b7280',
    marginBottom: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
  },
  categoryTabs: {
    marginHorizontal: 15,
    marginBottom: 15,
  },
  categoryTabsContent: {
    paddingRight: 15,
  },
  categoryTab: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  activeCategoryTab: {
    borderColor: '#f59e0b',
  },
  categoryTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6b7280',
  },
  activeCategoryTabText: {
    color: '#ffffff',
    fontWeight: '600',
  },
  certificatesSection: {
    paddingHorizontal: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
  sectionCount: {
    fontSize: 14,
    color: '#6b7280',
  },
  certificateCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  certificateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  certificateIcon: {
    width: 56,
    height: 56,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  certificateInfo: {
    flex: 1,
  },
  certificateTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
  },
  certificateSubtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 2,
  },
  certificateInstructor: {
    fontSize: 12,
    color: '#9ca3af',
  },
  verifiedBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  certificateDetails: {
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  detailText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 6,
  },
  credentialInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  credentialText: {
    fontSize: 12,
    color: '#6b7280',
    marginLeft: 6,
    fontFamily: 'monospace',
  },
  skillsSection: {
    marginBottom: 16,
  },
  skillsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    borderWidth: 1,
    marginRight: 6,
    marginBottom: 4,
  },
  skillText: {
    fontSize: 10,
    fontWeight: '500',
  },
  certificateActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 2,
    justifyContent: 'center',
  },
  viewButton: {
    // backgroundColor set dynamically
  },
  downloadButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  shareButton: {
    backgroundColor: '#f3f4f6',
  },
  verifyButton: {
    backgroundColor: '#f0fdf4',
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
    color: '#ffffff',
  },
  emptyState: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 12,
    marginBottom: 15,
  },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default LearningCertificateScreen;

